import { assignInlineVars } from '@vanilla-extract/dynamic';
import clsx from 'clsx';
import { useContext } from 'react';
import { useIntl } from 'react-intl';

import Colors from '@dynatrace/strato-design-tokens/colors';
import Spacings from '@dynatrace/strato-design-tokens/spacings';
import { PlusIcon, MinusIcon } from '@dynatrace/strato-icons';

import { SubrowCellContext } from './SubRowCellContext.js';
import {
  subRowExpandButtonCellWrapperCSS,
  subRowExpandButtonHeaderCellWrapperCSS,
  subRowExpanderButtonCSS,
  subRowExpandIndentSvgCSS,
  subRowExpandIndentSvgSizeCSS,
  subRowIconSize,
} from './SubRows.sty.js';
import { useFocusRing } from '../../../../core/styles/useFocusRing.js';
import {
  useDataTableV2,
  useDataTableV2Row,
} from '../../contexts/DataTableV2Context.js';

/**
 * @internal
 */
export function SubRowsHeaderExpander(props: { isAllRowsExpanded: boolean }) {
  const { isAllRowsExpanded } = props;
  const table = useDataTableV2();
  const intl = useIntl();

  const { focusClassName, focusProps } = useFocusRing();

  return (
    <button
      type="button"
      className={clsx(
        subRowExpandButtonHeaderCellWrapperCSS,
        subRowExpanderButtonCSS,
        focusClassName,
      )}
      onClick={() => {
        table.toggleAllRowsExpanded();
        const expandColumnId = table.getSubRowIndicatorColumnId();
        if (expandColumnId) {
          setTimeout(() => {
            table._resetHeaderMeasurement(expandColumnId);
          }, 0);
        }
      }}
      aria-label={intl.formatMessage(
        {
          defaultMessage:
            '{expandState, select, expand {Expand} other {Collapse}} all subrows',
          description: 'Describes the action of the sub rows trigger',
          id: 'YithM+0sfIXQLp9+',
        },
        {
          expandState: isAllRowsExpanded ? 'other' : 'expand',
        },
      )}
      style={{
        color: Colors.Text.Neutral.Subdued,
      }}
      {...focusProps}
    >
      {isAllRowsExpanded ? <MinusIcon /> : <PlusIcon />}
    </button>
  );
}

/**
 * Generates a svg path based on the shape patterns we want to draw.
 */
function generatePathFromConfig(
  config: Array<'Line' | 'TShape' | 'LShape' | 'Empty'>,
  spacer: number = 16,
): string {
  let pathString = '';
  for (const [index, shape] of config.entries()) {
    const oneBaseIndex = index + 1;
    const x = oneBaseIndex * spacer - spacer / 2;
    // Magic numbers like 300 are relative large values in order to make sure the scaled
    // viewbox of the svg is not cut off somewhere. These are random large values.
    if (shape === 'Line') {
      pathString += `M ${x},-300 V 300 `;
    } else if (shape === 'LShape') {
      pathString += `M ${x},-300 V 25 h 200 `;
    } else if (shape === 'TShape') {
      pathString += `M ${x},-300 V 300 m 0,-275 h 25`;
    }
  }
  return pathString;
}

/**
 * @internal
 */
export function SubRowsCellExpander() {
  const intl = useIntl();
  const table = useDataTableV2();
  const row = useDataTableV2Row();

  const { focusClassName, focusProps } = useFocusRing();
  const subrowContext = useContext(SubrowCellContext);

  if (!row) {
    return null;
  }

  const indentGrid = 16;
  const iconSize = 20;
  const width = indentGrid * row.depth;

  const rowCanExpand = subrowContext.canExpand;
  const rowCanExpandDisabled = subrowContext.canExpandDisabled;

  let density = Spacings.Size8;
  const configuredDensity = table.getVisualVariantOptions().rowDensity;
  if (configuredDensity === 'condensed') {
    density = Spacings.Size2;
  } else if (configuredDensity === 'comfortable') {
    density = Spacings.Size12;
  }

  return (
    <div
      className={clsx(subRowExpandButtonCellWrapperCSS)}
      style={{
        ...assignInlineVars({ [`${subRowIconSize}`]: `${iconSize}px` }),
        // Calculate the width based on icon button size (icon size + 2 * Size2 padding) and the padding to the right Size8
        width: `calc(${width + iconSize}px + ${density} + ${Spacings.Size4})`,
      }}
    >
      {row.depth > 0 ? (
        <svg
          className={clsx(
            subRowExpandIndentSvgCSS,
            subRowExpandIndentSvgSizeCSS({ rowCanExpand }),
          )}
          style={{
            width: rowCanExpand ? width : width + iconSize,
          }}
          preserveAspectRatio="none"
          overflow="visible"
          viewBox={`0 0 ${rowCanExpand ? width : width + iconSize} 50`}
        >
          <path
            d={generatePathFromConfig(table._getRowLineConfig()(row.id))}
            fill="transparent"
            vectorEffect="non-scaling-stroke"
            strokeWidth={1}
          />
        </svg>
      ) : null}

      {/* Only render a button if the row can actually expand */}
      {rowCanExpand ? (
        <button
          disabled={rowCanExpandDisabled}
          type="button"
          aria-label={intl.formatMessage(
            {
              defaultMessage:
                '{expandState, select, expand {Expand} other {Collapse}} subrow with id "{id}".',
              description: 'Describes the action of the sub rows trigger',
              id: 'kCk8ZqQbTzdkw3WN',
            },
            {
              expandState: subrowContext.isExpanded ? 'other' : 'expand',
              id: row.id,
            },
          )}
          aria-expanded={subrowContext.isExpanded}
          className={clsx(subRowExpanderButtonCSS, focusClassName)}
          onClick={() => {
            row.toggleExpanded(!subrowContext.isExpanded);

            const expandColumnId = table.getSubRowIndicatorColumnId();
            if (expandColumnId) {
              setTimeout(() => {
                table._resetHeaderMeasurement(expandColumnId);
              }, 0);
            }
          }}
          {...focusProps}
        >
          {subrowContext.isExpanded ? (
            <MinusIcon size={iconSize} />
          ) : (
            <PlusIcon
              size={iconSize}
              // Turn visibility to hidden in order to avoid a relayout shift
              // when no icon is shown
              style={{
                visibility: subrowContext.canExpand ? 'visible' : 'hidden',
              }}
            />
          )}
        </button>
      ) : null}
    </div>
  );
}
